import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Textarea } from '@/components/ui/Textarea';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import type { PlatformFeature } from '@/types/platformFeature';
import { featuresApi } from '../api/features.api';
import { catalogueEditSchema, catalogueEntrySchema } from '../schemas/feature.schema';

interface CatalogueEntryModalProps {
  isOpen: boolean;
  /** The row being corrected, or null to add a new one. */
  feature: PlatformFeature | null;
  onClose: () => void;
  /** Fired after a successful save, so the caller can read the board again. */
  onSaved: () => void;
}

/** Price is held as typed — a string — and parsed by the schema on save. */
type Draft = { key: string; name: string; description: string; price: string };
type FieldErrors = Partial<Record<keyof Draft, string>>;

const BLANK: Draft = { key: '', name: '', description: '', price: '' };

/**
 * The catalogue form — one component for adding a feature and for correcting
 * one.
 *
 * The two differ by a single field and a single call, so this takes a variant
 * rather than being copied and edited (CLAUDE.md §3). The key is the field:
 * writable while the row is being created, read-only afterwards, because it is
 * what code would gate on and renaming it would leave anything reading it
 * looking for something that is no longer there — which is exactly what the
 * server's update schema refuses.
 */
export function CatalogueEntryModal({
  isOpen,
  feature,
  onClose,
  onSaved,
}: CatalogueEntryModalProps) {
  const isEditing = feature !== null;

  const [draft, setDraft] = useState<Draft>(BLANK);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [isSaving, setIsSaving] = useState(false);

  // Reloaded whenever the dialog opens on a different row, so it never shows
  // the last feature somebody looked at.
  useEffect(() => {
    if (!isOpen) return;
    setDraft(
      feature
        ? {
            key: feature.key,
            name: feature.name,
            description: feature.description,
            price: feature.price ?? '',
          }
        : BLANK,
    );
    setErrors({});
  }, [isOpen, feature]);

  function close() {
    if (isSaving) return;
    onClose();
  }

  async function save() {
    const schema = isEditing ? catalogueEditSchema : catalogueEntrySchema;
    const parsed = schema.safeParse(draft);

    if (!parsed.success) {
      const fields: FieldErrors = {};
      for (const issue of parsed.error.issues) {
        const field = issue.path[0] as keyof Draft;
        // First message per field: a second one under the same input would only
        // replace the first before anybody read it.
        if (!fields[field]) fields[field] = issue.message;
      }
      setErrors(fields);
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        const saved = await featuresApi.updateInCatalogue(feature.uuid, {
          name: draft.name.trim(),
          description: draft.description.trim(),
          price: parsed.data.price,
        });
        toast.success(`${saved.name} has been updated`, 'Every institute sees the new wording.');
      } else {
        const created = await featuresApi.addToCatalogue({
          key: draft.key.trim(),
          name: draft.name.trim(),
          description: draft.description.trim(),
          price: parsed.data.price,
        });
        toast.success(`${created.name} is in the catalogue`, 'It now appears on every institute.');
      }

      onSaved();
      onClose();
    } catch (caught) {
      // The server's messages name the reason — a key already taken, a feature
      // that has gone — so they are shown as they are written.
      toast.error(
        isEditing ? 'Could not save that change' : 'Could not add that feature',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      title={isEditing ? 'Edit feature' : 'New feature'}
      description={
        isEditing
          ? 'The wording every institute’s tracker reads.'
          : 'Adds a row to the catalogue every institute’s tracker reads.'
      }
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={() => void save()} isLoading={isSaving}>
            {isEditing ? 'Save changes' : 'Add to catalogue'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Input
          label="Name"
          placeholder="Fee reminders"
          value={draft.name}
          error={errors.name}
          onChange={(event) => setDraft((current) => ({ ...current, name: event.target.value }))}
        />

        <Input
          label="Key"
          hint={
            isEditing
              ? 'Fixed — this is what code would gate on.'
              : 'Fixed once saved — this is what code would gate on.'
          }
          placeholder="FEE_REMINDERS"
          value={draft.key}
          error={errors.key}
          disabled={isEditing}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              // Shaped as it is typed: spaces and hyphens become underscores and
              // anything else the server's key rule refuses is dropped, so
              // "fee reminders" arrives as FEE_REMINDERS rather than as an error.
              key: event.target.value
                .toUpperCase()
                .replace(/[\s-]+/g, '_')
                .replace(/[^A-Z0-9_]/g, ''),
            }))
          }
        />

        <Input
          label="Price (optional)"
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          placeholder="15000"
          hint="In ₹. Leave blank when it is part of the base licence."
          value={draft.price}
          error={errors.price}
          onChange={(event) => setDraft((current) => ({ ...current, price: event.target.value }))}
        />

        <Textarea
          label="Description"
          rows={3}
          placeholder="What the customer gets, in one sentence."
          value={draft.description}
          error={errors.description}
          onChange={(event) =>
            setDraft((current) => ({ ...current, description: event.target.value }))
          }
        />
      </div>
    </Modal>
  );
}
